import {useAgendaCategoryPatientAgeRange} from '@/features/agenda/hooks/useAgendaCategoryPatientAgeRange'
import {useAgendaCategoryTeam} from '@/features/agenda/hooks/useAgendaCategoryTeam'
import {useAgendaSearchParams} from '@/features/agenda/hooks/useAgendaSearchParams'
import {HttpGetEventsOptions} from '@/features/agenda/http/agenda.http'
import {AgendaUrlParams} from '@/features/agenda/types'
import {calculateAgendaDates} from '@/features/agenda/utils'
import {useAuthStore} from '@/features/auth/store/store'
import {useMemo} from 'react'

/**
 * Hook that builds query parameters for agenda events based on URL params and search params
 */
export const useAgendaQueryParams = ({date, view, entity}: AgendaUrlParams): HttpGetEventsOptions['params'] => {
    const currentUser = useAuthStore(state => state.user)
    const {
        searchParams: {subjectIds, patientId, specializationIds},
        isTeamCategory,
        isPatientAgeRangeCategory,
        isCustomCategory
    } = useAgendaSearchParams()

    const {team} = useAgendaCategoryTeam()
    const {users: specializationUsers} = useAgendaCategoryPatientAgeRange()

    // Calculate start and end dates from date and view using the utility function
    const calculatedDates = useMemo(() => calculateAgendaDates(date, view), [date, view])

    // Check if the category set as search param is running
    const isRunningCategory = useMemo(() => {
        return (
            (isTeamCategory && patientId) ||
            (isPatientAgeRangeCategory && specializationIds) ||
            (isCustomCategory && subjectIds)
        )
    }, [isTeamCategory, patientId, isPatientAgeRangeCategory, specializationIds, isCustomCategory, subjectIds])

    // Build the IDs array based on search mode
    const therapists = useMemo(() => {
        // For 'users' entity, use selected subjects or fall back to current user
        if (entity == 'users') {
            return subjectIds?.length
                ? subjectIds
                : team?.length
                  ? team.map(teamMember => teamMember.id)
                  : specializationUsers?.length
                    ? specializationUsers.map(user => user.id)
                    : currentUser?.id && !isRunningCategory
                      ? [currentUser.id]
                      : []
        }

        // For other entities (patients), use current user only if no subjects are selected
        return !subjectIds?.length && currentUser?.id ? [currentUser.id] : []
    }, [subjectIds, currentUser?.id, entity, team, specializationUsers])

    const patients = useMemo(() => {
        return entity == 'patients' ? (subjectIds && subjectIds.length > 0 ? subjectIds : []) : []
    }, [subjectIds])

    // Build the complete params object
    const queryParams = useMemo(
        () => ({
            startDate: calculatedDates.startDate,
            endDate: calculatedDates.endDate,
            therapists,
            patients
        }),
        [calculatedDates, therapists, patients]
    )

    return queryParams
}
